/**
 * 浏览器端图片相关通用辅助（加载、base64/文件互转、尺寸计算、Canvas 方向绘制、耗时与体积日志格式化等）。
 * 由 common 聚合导出，业务代码仍通过 @/utils/common 使用。
 */

import report from '../report.js';
import slog from '../slog.js';
import Store from '../Store.js';
import { isDev, isDevPrintLog } from '../config.js';

const atob = require('atob');

/**
 * 读取抖音环境数据（与 common 内 getDouyinData 语义一致，供上报字段使用）
 * @returns {Object|null}
 */
function getDouyinDataForReport() {
  let localDyData = null;
  try {
    localDyData = Store.getSession('dyData');
  } catch (error) {}
  return window._dyData || localDyData;
}

/**
 * 是否抖音小程序环境（与 common 内 isDouyinApp 判断一致）
 * @returns {boolean}
 */
function isDouyinAppForReport() {
  var dyData = getDouyinDataForReport();
  return !!(dyData && dyData.orderNo);
}

/**
 * 开发环境下带样式的控制台耗时/参数日志
 * @param {...*} args - 任意参数，File/Blob 会格式化为可读字符串
 * @returns {void}
 */
export function logTime() {
  var args = arguments;
  try {
    var processedArgs = Array.prototype.slice.call(args).map(function (arg) {
      if (arg instanceof File || arg instanceof Blob) {
        var mb = Math.round(arg.size / 1024 / 1024);
        return (
          'File[' + (arg.name || 'blob') + '] (' + arg.type + ', ' + mb + 'mb)'
        );
      }
      return arg;
    });
    var message = processedArgs.join(' ');
    if (isDevPrintLog) {
      console.log(
        '%c' + message,
        'background: #47c68c; color: white; font-size: 16px; font-weight: bold;'
      );
    }
  } catch (error) {
    console.log.apply(console, args);
  }
}

/**
 * 将毫秒数格式化为用于上报的档位（小于 1s 按 100ms，否则按秒级）
 * @param {number} millisecond - 毫秒
 * @returns {number}
 */
export function formatLogTime(millisecond) {
  if (millisecond < 1000) {
    return Math.round(millisecond / 100) * 100;
  }
  return Math.round(millisecond / 1000) * 1000;
}

/**
 * 将字节数格式化为用于上报的 MB 整数（至少为 1）
 * @param {number} fileSize - 字节
 * @returns {number}
 */
export function formatFileSize(fileSize) {
  var mb = Math.round(fileSize / 1024 / 1024);
  if (mb < 1) {
    return 1;
  }
  return mb;
}

/**
 * 图片加载失败时通过 XHR 拉取并二次解码，用于上报错误原因
 * @param {string} imageUrl - 图片地址
 * @returns {Promise<HTMLImageElement>}
 */
export function _getImageErrorCodeInfo(imageUrl) {
  var startTime = Date.now();
  if (!imageUrl && isDev) {
    // debugger;
  }
  var reportUrl = imageUrl;
  try {
    if (imageUrl.indexOf('blob:http') > -1) {
      reportUrl = 'blob:';
    }
  } catch (error) {}
  return new Promise(function (resolve, reject) {
    var rejectHandler = function (reason) {
      reject(new Error(reason));
      var isPageRefreshed = window._isPageRefreshed ? 1 : 0;
      report('loadImage_fail', {
        imageUrl: reportUrl,
        reason: reason,
        msg:
          'imageUrl=' +
          reportUrl +
          ',reason=' +
          reason +
          ',isDouyinApp=' +
          isDouyinAppForReport() +
          ',isPageRefreshed=' +
          isPageRefreshed,
        isPageRefreshed: isPageRefreshed
      });
    };
    var xhr = new XMLHttpRequest();
    xhr.open('GET', imageUrl, true);
    xhr.onload = function () {
      if (xhr.status >= 200 && xhr.status < 300) {
        var img = new Image();
        if (/http/.test(imageUrl)) {
          img.crossOrigin = 'anonymous';
        }
        img.onload = function () {
          if (img.naturalWidth) {
            img.width = img.naturalWidth;
            img.height = img.naturalHeight;
          }
          resolve(img);
          logTime('_getImageErrorCodeInfo耗时', new Date() - startTime + 'ms');
        };
        img.onerror = function (err) {
          rejectHandler('Image decode failed ' + xhr.status);
        };
        img.src = imageUrl;
      } else {
        rejectHandler('HTTP ' + xhr.status + ' ' + xhr.statusText);
      }
    };
    xhr.onerror = function () {
      rejectHandler('Network error ' + xhr.status);
    };
    xhr.send();
  });
}

/**
 * 加载图片为 HTMLImageElement（跨域 Anonymous，失败时触发错误信息收集）
 * @param {string} src - 图片 URL / blob URL / data URL
 * @returns {Promise<HTMLImageElement>}
 */
export function loadImage(src) {
  return new Promise(function (resolve, reject) {
    var img = new Image();
    img.setAttribute('crossOrigin', 'Anonymous');
    img.onload = function () {
      resolve(img);
    };
    img.onerror = function (err) {
      reject(err);
      _getImageErrorCodeInfo(src);
    };
    img.src = src;
  });
}

/** 与 fileToBase64 共用的 base64 尾片段缓存（由 common 内 fileToBase64 写入） */
export var base64ToFileCache = {};

/**
 * Data URL 转 File
 * @param {string} dataUrl - data:image/...;base64,...
 * @param {string} [filename='1.png'] - 文件名
 * @returns {File}
 */
export function base64ToFile(dataUrl, filename) {
  filename = filename === undefined ? '1.png' : filename;
  try {
    var cacheKey = dataUrl.slice(-100, dataUrl.length);
    if (base64ToFileCache[cacheKey] && 0) {
      return base64ToFileCache[cacheKey];
    }
    var arr = dataUrl.split(',');
    var mime = arr[0].match(/:(.*?);/)[1];
    var bstr = atob(arr[1]);
    var n = bstr.length;
    var u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    var file = new File([u8arr], filename, { type: mime });
    u8arr = null;
    return file;
  } catch (error) {
    slog.log('base64ToFile', error);
    throw error;
  }
}

/**
 * 按最大宽高等比缩放后的绘制尺寸
 * @param {number} width - 原始宽
 * @param {number} height - 原始高
 * @param {number} maxWidth - 限制最大宽
 * @param {number} maxHeight - 限制最大高
 * @returns {{ width: number, height: number }}
 */
export function calcMaxSize(width, height, maxWidth, maxHeight) {
  var ratio = width / height;
  var w;
  var h;
  // 输入或限制尺寸无效时返回 0 尺寸，避免调用方生成负数画布。
  if (!isFinite(ratio) || ratio <= 0 || maxWidth <= 0 || maxHeight <= 0) {
    return {
      width: 0,
      height: 0
    };
  }
  if (ratio > maxWidth / maxHeight) {
    // 先四舍五入算出短边，再反推长边，使最终比例更接近原始比例
    h = Math.floor(maxWidth / ratio);
    w = Math.round(h * ratio);
  } else {
    // 先四舍五入算出短边，再反推长边
    w = Math.floor(maxHeight * ratio);
    h = Math.round(w / ratio);
  }
  // 反推后可能略微超出最大值，确保不越界
  if (w > maxWidth) w = maxWidth;
  if (h > maxHeight) h = maxHeight;
  return {
    width: w,
    height: h
  };
}

/**
 * Blob URL 下载为 File
 * @param {string} blobUrl - blob: 地址
 * @param {string|number} [filename] - 文件名前缀，默认时间戳
 * @returns {Promise<File>}
 */
export function blobUrlToFile(blobUrl, filename) {
  filename = filename === undefined ? +new Date() : filename;
  return new Promise(function (resolve, reject) {
    var xhr = new XMLHttpRequest();
    xhr.open('GET', blobUrl, true);
    xhr.responseType = 'blob';
    xhr.onload = function () {
      if (xhr.status === 200) {
        var blob = xhr.response;
        var type = blob.type.split('/')[1];
        var file = new File([blob], filename + '.' + type, {
          type: blob.type,
          lastModified: Date.now()
        });
        resolve(file);
      } else {
        reject(new Error('请求失败'));
      }
    };
    xhr.onerror = function () {
      reject(new Error('请求错误'));
    };
    xhr.send();
  });
}

/**
 * 按 EXIF orientation（1–8）设置画布尺寸，并在 2d 上下文上完成变换后绘制图片。
 * @param {HTMLCanvasElement} canvas - 目标画布（会修改 width/height）
 * @param {CanvasImageSource} img - 已解码的图片（如 HTMLImageElement）
 * @param {number} orientation - EXIF 方向 1–8，1 为正常
 * @param {number} scaledWidth - 经 maxWidthOrHeight 缩放后的逻辑绘制宽度
 * @param {number} scaledHeight - 经 maxWidthOrHeight 缩放后的逻辑绘制高度
 * @param {number} naturalWidth - 图片原始像素宽
 * @param {number} naturalHeight - 图片原始像素高
 * @returns {void}
 */
export function drawImageWithOrientationToCanvas(
  canvas,
  img,
  orientation,
  scaledWidth,
  scaledHeight,
  naturalWidth,
  naturalHeight
) {
  var canvasWidth = scaledWidth;
  var canvasHeight = scaledHeight;
  var originalCanvasWidth = scaledWidth;
  var originalCanvasHeight = scaledHeight;

  canvas.width = canvasWidth;
  canvas.height = canvasHeight;

  if ([5, 6, 7, 8].indexOf(orientation) > -1) {
    var tmpW = canvasWidth;
    canvasWidth = originalCanvasHeight;
    canvasHeight = tmpW;
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
  }

  var ctx = canvas.getContext('2d');
  ctx.save();
  switch (orientation) {
    case 2:
      ctx.translate(canvasWidth, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);
      break;
    case 3:
      ctx.translate(canvasWidth / 2, canvasHeight / 2);
      ctx.rotate(Math.PI);
      ctx.drawImage(
        img,
        0,
        0,
        naturalWidth,
        naturalHeight,
        -canvasWidth / 2,
        -canvasHeight / 2,
        canvasWidth,
        canvasHeight
      );
      break;
    case 4:
      ctx.translate(0, canvasHeight);
      ctx.scale(1, -1);
      ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);
      break;
    case 5:
      ctx.translate(canvasWidth / 2, canvasHeight / 2);
      ctx.scale(-1, 1);
      ctx.rotate(Math.PI / 2);
      ctx.drawImage(
        img,
        0,
        0,
        naturalWidth,
        naturalHeight,
        -canvasHeight / 2,
        -canvasWidth / 2,
        canvasHeight,
        canvasWidth
      );
      break;
    case 6:
      ctx.translate(canvasWidth / 2, canvasHeight / 2);
      ctx.rotate(Math.PI / 2);
      ctx.drawImage(
        img,
        0,
        0,
        naturalWidth,
        naturalHeight,
        -canvasHeight / 2,
        -canvasWidth / 2,
        canvasHeight,
        canvasWidth
      );
      break;
    case 7:
      ctx.translate(canvasWidth / 2, canvasHeight / 2);
      ctx.scale(1, -1);
      ctx.rotate(Math.PI / 2);
      ctx.drawImage(
        img,
        0,
        0,
        naturalWidth,
        naturalHeight,
        -canvasHeight / 2,
        -canvasWidth / 2,
        canvasHeight,
        canvasWidth
      );
      break;
    case 8:
      ctx.translate(canvasWidth / 2, canvasHeight / 2);
      ctx.rotate(-Math.PI / 2);
      ctx.drawImage(
        img,
        0,
        0,
        naturalWidth,
        naturalHeight,
        -canvasHeight / 2,
        -canvasWidth / 2,
        canvasHeight,
        canvasWidth
      );
      break;
    default:
      ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);
  }
  ctx.restore();
}
